import { useRef, useState, useEffect } from 'react';
import { Jellyfish } from '@/effects';

/**
 * OceanScene — 共享海洋动态场景组件
 * 
 * 可在任何页面使用。包含以下视差层：
 *   Layer 0: 深海背景图 (4px / 3px)
 *   Layer 1: 星空 (8px / 6px)
 *   Layer 2: 海雾/海面光影 (12px / 9px)
 *   Layer 3: 鱼群 (16px / 12px)
 *   Layer 4: 前景漂浮颗粒 (22px / 16px)
 * 
 * 所有层使用统一阻尼插值 (1 - Math.pow(0.05, dt))
 * 
 * Props:
 *   showBrandText: 是否显示品牌文案
 *  rightGradient: 是否在右侧添加渐变（用于登录页分栏）
 *   className: 额外类名
 */

// ----- 星空背景 -----
function StarField() {
  const [stars] = useState(() =>
    Array.from({ length: 60 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 55,
      size: 1 + Math.random() * 1.5,
      delay: Math.random() * 4,
      duration: 3 + Math.random() * 4,
      opacity: 0.3 + Math.random() * 0.7,
    }))
  );

  return (
    <div className="absolute inset-0 z-[1] pointer-events-none overflow-hidden">
      {stars.map((s) => (
        <div
          key={s.id}
          className="absolute rounded-full"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            backgroundColor: `rgba(200, 220, 255, ${s.opacity})`,
            animation: `ocean-star-twinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
            boxShadow: `0 0 ${s.size * 2}px rgba(200, 220, 255, ${s.opacity * 0.5})`,
          }}
        />
      ))}
    </div>
  );
}

// ----- 漂浮颗粒 -----
function FloatingParticles() {
  const containerRef = useRef(null);
  const particlesRef = useRef([]);
  const animRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const W = container.offsetWidth || 800;
    const H = container.offsetHeight || 600;

    particlesRef.current = Array.from({ length: 25 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      size: 1.5 + Math.random() * 2.5,
      speed: 0.2 + Math.random() * 0.35,
      opacity: 0.08 + Math.random() * 0.2,
      phase: Math.random() * Math.PI * 2,
      driftSpeed: 0.3 + Math.random() * 0.5,
      driftAmp: 4 + Math.random() * 8,
    }));

    let lastTime = performance.now();

    const tick = (now) => {
      const dt = Math.min((now - lastTime) / 16.67, 3);
      lastTime = now;
      const h = container.offsetHeight || 600;
      const w = container.offsetWidth || 800;
      const nodes = container.children;

      particlesRef.current.forEach((p, i) => {
        p.y -= p.speed * dt;
        p.x += Math.sin(now * 0.001 * p.driftSpeed + p.phase) * p.driftAmp * dt * 0.04;

        if (p.y < -10) {
          p.y = h + 10;
          p.x = Math.random() * w;
        }

        const node = nodes[i];
        if (node) {
          node.style.transform = `translate3d(${p.x}px, ${p.y}px, 0)`;
          node.style.opacity = p.opacity;
        }
      });

      animRef.current = requestAnimationFrame(tick);
    };

    animRef.current = requestAnimationFrame(tick);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-[6] pointer-events-none overflow-hidden"
    >
      {Array.from({ length: 25 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full will-change-transform"
          style={{
            width: `${1.5 + (i % 3)}px`,
            height: `${1.5 + (i % 3)}px`,
            background: 'radial-gradient(circle, rgba(120, 200, 240, 0.7) 0%, rgba(88, 183, 232, 0) 70%)',
            boxShadow: '0 0 4px rgba(120, 200, 240, 0.3)',
          }}
        />
      ))}
    </div>
  );
}

// ----- 海面光影 -----
function OceanSurfaceGlow() {
  return (
    <div
      className="absolute inset-x-0 bottom-0 h-[40%] z-[1] pointer-events-none"
      style={{
        background: [
          'linear-gradient(180deg,',
          'rgba(6, 24, 42, 0) 0%,',
          'rgba(10, 40, 70, 0.06) 30%,',
          'rgba(15, 55, 95, 0.1) 60%,',
          'rgba(8, 29, 50, 0.18) 100%)',
        ].join(''),
        animation: 'ocean-shimmer 12s ease-in-out infinite',
      }}
    >
      <div
        className="absolute top-0 left-[15%] w-[45%] h-[60%]"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(88, 183, 232, 0.08) 0%, transparent 70%)',
          animation: 'ocean-light-drift 18s ease-in-out infinite',
        }}
      />
      <div
        className="absolute top-[15%] left-[50%] w-[30%] h-[40%]"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(242, 200, 100, 0.04) 0%, transparent 70%)',
          animation: 'ocean-light-drift 22s ease-in-out infinite reverse',
        }}
      />
    </div>
  );
}

// ----- 灯塔扫光 -----
function LighthouseBeam() {
  const beamRef = useRef(null);
  const glowRef = useRef(null);

  useEffect(() => {
    let animId;
    const start = performance.now();

    const tick = (now) => {
      const elapsed = (now - start) % 6000;

      if (elapsed < 1800) {
        const progress = elapsed / 1800;
        const intensity = Math.sin(progress * Math.PI);
        if (beamRef.current) {
          beamRef.current.style.opacity = intensity * 0.2;
          beamRef.current.style.transform = `rotate(${-12 + progress * 24}deg)`;
        }
        if (glowRef.current) {
          glowRef.current.style.opacity = 0.2 + intensity * 0.8;
        }
      } else {
        if (beamRef.current) beamRef.current.style.opacity = 0;
        if (glowRef.current) glowRef.current.style.opacity = 0.15;
      }

      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, []);

  return (
    <div
      className="absolute z-[4] pointer-events-none"
      style={{ bottom: '12%', right: '22%', width: '35%', height: '50%' }}
    >
      <div
        ref={beamRef}
        className="absolute bottom-0 left-1/2 w-full h-full will-change-transform"
        style={{ transformOrigin: 'bottom center', opacity: 0 }}
      >
        <svg viewBox="0 0 100 200" preserveAspectRatio="none" className="w-full h-full" style={{ overflow: 'visible' }}>
          <defs>
            <linearGradient id="oceanBeamGrad" x1="50" y1="200" x2="50" y2="0" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stopColor="rgba(242, 200, 100, 0.8)" />
              <stop offset="30%" stopColor="rgba(242, 185, 75, 0.4)" />
              <stop offset="70%" stopColor="rgba(242, 185, 75, 0.1)" />
              <stop offset="100%" stopColor="rgba(242, 185, 75, 0)" />
            </linearGradient>
          </defs>
          <polygon points="48,200 52,200 80,0 20,0" fill="url(#oceanBeamGrad)" />
        </svg>
      </div>

      <div
        ref={glowRef}
        className="absolute bottom-0 left-1/2 w-3 h-3 rounded-full will-change-transform"
        style={{
          transform: 'translate(-50%, 50%)',
          background: 'radial-gradient(circle, rgba(242,200,100,0.9) 0%, rgba(242,200,100,0.4) 40%, transparent 70%)',
          boxShadow: '0 0 12px rgba(242,200,100,0.6)',
          filter: 'blur(1px)',
          opacity: 0.15,
        }}
      />
    </div>
  );
}

// ----- 鱼群 -----
function FishLayer() {
  const [fishes] = useState(() => {
    const layers = [
      { count: 5, sizeRange: [10, 14], speed: 12, opacity: [0.2, 0.35], yRange: [0.12, 0.32], direction: -1 },
      { count: 7, sizeRange: [12, 18], speed: 18, opacity: [0.25, 0.45], yRange: [0.22, 0.52], direction: 1 },
      { count: 4, sizeRange: [16, 24], speed: 25, opacity: [0.35, 0.6], yRange: [0.38, 0.68], direction: -1 },
      { count: 3, sizeRange: [20, 30], speed: 30, opacity: [0.45, 0.8], yRange: [0.52, 0.82], direction: 1 },
    ];

    const result = [];
    layers.forEach((layer, li) => {
      for (let i = 0; i < layer.count; i++) {
        result.push({
          id: `fish-${li}-${i}`,
          x: Math.random() * 100,
          y: layer.yRange[0] + Math.random() * (layer.yRange[1] - layer.yRange[0]),
          size: layer.sizeRange[0] + Math.random() * (layer.sizeRange[1] - layer.sizeRange[0]),
          opacity: layer.opacity[0] + Math.random() * (layer.opacity[1] - layer.opacity[0]),
          speed: layer.speed * (0.7 + Math.random() * 0.6),
          direction: layer.direction,
          wobblePhase: Math.random() * Math.PI * 2,
          wobbleSpeed: 0.5 + Math.random() * 1,
          wobbleAmp: 3 + Math.random() * 5,
        });
      }
    });
    return result;
  });

  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let lastTime = performance.now();
    let animId;

    const tick = (now) => {
      const dt = Math.min((now - lastTime) / 1000, 0.1);
      lastTime = now;
      const nodes = container.children;

      fishes.forEach((fish, i) => {
        fish.x += fish.direction * fish.speed * dt * 0.02;
        if (fish.x > 110) fish.x = -10;
        if (fish.x < -10) fish.x = 110;

        const wobble = Math.sin(now * 0.001 * fish.wobbleSpeed + fish.wobblePhase) * fish.wobbleAmp;

        const node = nodes[i];
        if (node) {
          node.style.transform = `translate3d(${fish.x}%, ${wobble}px, 0) scaleX(${fish.direction})`;
        }
      });

      animId = requestAnimationFrame(tick);
    };

    animId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animId);
  }, [fishes]);

  return (
    <div ref={containerRef} className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
      {fishes.map((fish) => (
        <div
          key={fish.id}
          className="absolute will-change-transform"
          style={{
            top: `${fish.y * 100}%`,
            left: 0,
            width: `${fish.size}px`,
            height: `${fish.size * 0.5}px`,
            opacity: fish.opacity,
          }}
        >
          <img
            src="/assets/ocean/fish/fish-blue.svg"
            alt=""
            className="w-full h-full"
            style={{ objectFit: 'contain' }}
          />
        </div>
      ))}
    </div>
  );
}

// ===== 主组件 =====
export default function OceanScene({ showBrandText = true, rightGradient = true, className = '' }) {
  const containerRef = useRef(null);
  const layerRefs = useRef([]);
  const [entered, setEntered] = useState(false);

  // 页面进入
  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) { setEntered(true); return; }
    const timer = setTimeout(() => setEntered(true), 80);
    return () => clearTimeout(timer);
  }, []);

  // 鼠标视差 — 阻尼插值应用于多层
  useEffect(() => {
    if ('ontouchstart' in window) return;

    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    let lastTime = 0;
    let frame;

    const tick = (time) => {
      const dt = Math.min((time - lastTime) / 1000, 0.1);
      lastTime = time;
      const damping = 1 - Math.pow(0.05, dt);
      mouse.x += (mouse.tx - mouse.x) * damping;
      mouse.y += (mouse.ty - mouse.y) * damping;

      // 每层不同速度
      if (layerRefs.current[0]) layerRefs.current[0].style.transform = `translate(${mouse.x * -4}px, ${mouse.y * -3}px) scale(1.05)`;
      if (layerRefs.current[1]) layerRefs.current[1].style.transform = `translate(${mouse.x * -8}px, ${mouse.y * -6}px)`;
      if (layerRefs.current[2]) layerRefs.current[2].style.transform = `translate(${mouse.x * -12}px, ${mouse.y * -9}px)`;
      if (layerRefs.current[3]) layerRefs.current[3].style.transform = `translate(${mouse.x * -16}px, ${mouse.y * -12}px)`;
      if (layerRefs.current[4]) layerRefs.current[4].style.transform = `translate(${mouse.x * -22}px, ${mouse.y * -16}px)`;

      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);

    const handler = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      mouse.tx = (e.clientX - rect.left) / rect.width - 0.5;
      mouse.ty = (e.clientY - rect.top) / rect.height - 0.5;
    };

    const container = containerRef.current;
    if (container) container.addEventListener('mousemove', handler);

    return () => {
      cancelAnimationFrame(frame);
      if (container) container.removeEventListener('mousemove', handler);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden ${className}`}
      style={{ minHeight: '100vh' }}
    >
      {/* Layer 0: 深海背景图 — 最慢移动 */}
      <div
        ref={el => layerRefs.current[0] = el}
        className="absolute inset-0 z-0 will-change-transform"
        style={{
          opacity: entered ? 1 : 0,
          animation: entered ? 'ocean-bg-reveal 2s cubic-bezier(0.22,0.61,0.36,1) both' : 'none',
        }}
      >
        <img
          src="/bg-deep-sea.png"
          alt=""
          className="absolute object-cover"
          style={{
            top: '-3%', left: '-3%', width: '106%', height: '106%',
            filter: 'saturate(0.5) brightness(0.5) contrast(1.1)',
          }}
        />
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, rgba(6,18,34,0.3) 0%, rgba(6,18,34,0.05) 40%, rgba(6,18,34,0.1) 60%, rgba(6,18,34,0.45) 100%)',
        }} />
        {rightGradient && (
          <div className="absolute inset-0" style={{
            background: 'linear-gradient(90deg, transparent 0%, transparent 50%, rgba(6,18,34,0.3) 70%, rgba(6,18,34,0.6) 100%)',
          }} />
        )}
      </div>

      {/* Layer 1: 星空 */}
      <div
        ref={el => layerRefs.current[1] = el}
        className="absolute inset-0 will-change-transform"
        style={{ opacity: entered ? 1 : 0, transition: 'opacity 1.5s ease 0.2s' }}
      >
        <StarField />
      </div>

      {/* Layer 2: 海雾 + 海面光影 */}
      <div
        ref={el => layerRefs.current[2] = el}
        className="absolute inset-0 will-change-transform"
        style={{ opacity: entered ? 1 : 0, transition: 'opacity 1.2s ease 0.4s' }}
      >
        <OceanSurfaceGlow />
      </div>

      {/* Layer 3: 鱼群 */}
      <div
        ref={el => layerRefs.current[3] = el}
        className="absolute inset-0 will-change-transform"
        style={{ opacity: entered ? 1 : 0, transition: 'opacity 1s ease 0.6s' }}
      >
        <FishLayer />
      </div>

      {/* Layer 4: 前景颗粒 */}
      <div
        ref={el => layerRefs.current[4] = el}
        className="absolute inset-0 will-change-transform"
        style={{ opacity: entered ? 1 : 0, transition: 'opacity 1.2s ease 1s' }}
      >
        <FloatingParticles />
      </div>

      {/* 灯塔扫光 */}
      <div
        className="absolute inset-0 z-[5] pointer-events-none"
        style={{ opacity: entered ? 1 : 0, transition: 'opacity 0.8s ease 1.2s' }}
      >
        <LighthouseBeam />
      </div>

      {/* 水母 */}
      <div className="absolute z-[3] pointer-events-none" style={{ top: '16%', left: '10%', opacity: entered ? 1 : 0, transition: 'opacity 1.5s ease 1.5s' }}>
        <Jellyfish color="rgba(135, 230, 255, 0.35)" size={36} delay={0} duration={10} />
      </div>
      <div className="absolute z-[3] pointer-events-none" style={{ top: '30%', left: '36%', opacity: entered ? 1 : 0, transition: 'opacity 1.5s ease 1.8s' }}>
        <Jellyfish color="rgba(100, 210, 255, 0.25)" size={26} delay={2} duration={13} />
      </div>

      {/* 品牌文案 */}
      {showBrandText && (
        <div
          className="absolute z-[10] pointer-events-none"
          style={{ bottom: '8%', left: '6%', maxWidth: '380px', opacity: entered ? 1 : 0, transition: 'opacity 1.2s ease 1.8s' }}
        >
          <p
            className="text-base md:text-lg leading-relaxed font-light"
            style={{
              color: 'rgba(180, 200, 220, 0.85)',
              fontFamily: "'Noto Serif SC', Georgia, serif",
              letterSpacing: '0.02em',
              lineHeight: 1.8,
            }}
          >
            每一段旅程，
            <br />
            都有值得记录的瞬间。
          </p>
          <p className="text-sm mt-3 font-light" style={{
            color: 'rgba(143, 169, 192, 0.55)',
            letterSpacing: '0.02em',
          }}>
            记录每一段旅行，书写属于你的旅行故事
          </p>
        </div>
      )}

      <style>{`
        @keyframes ocean-bg-reveal {
          from { opacity: 0; transform: scale(1.03); filter: brightness(0.3); }
          to   { opacity: 1; transform: scale(1); filter: brightness(1); }
        }
        @keyframes ocean-star-twinkle {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
        @keyframes ocean-shimmer {
          0%, 100% { opacity: 0.9; transform: translateX(0); }
          50% { opacity: 1; transform: translateX(8px); }
        }
        @keyframes ocean-light-drift {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.8; }
          33% { transform: translate(15px, -8px) scale(1.1); opacity: 1; }
          66% { transform: translate(-10px, 5px) scale(0.95); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}
